import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BrainCircuitIcon, BotIcon, SparklesIcon, ZapIcon, SearchIcon, WorkflowIcon, ClockIcon, DollarSignIcon, TrendingUpIcon, FileSearchIcon, CheckCircleIcon, ClipboardCheckIcon, RocketIcon, BarChartIcon, CheckIcon, CloudIcon, NetworkIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
export function AIServicesPage() {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = [{
    title: 'IT Service Management',
    useCases: [{
      title: 'Intelligent Incident Resolution',
      challenge: 'IT agents spend 25-30 minutes per incident manually reading logs, searching for similar past incidents, and gathering user details before even starting resolution.',
      solution: 'AI-powered assistant automatically:\n• Summarizes incident details from user description and system logs\n• Identifies and retrieves similar past incidents with resolutions\n• Recommends resolution steps based on historical success rates\n• Drafts initial response to user\n• Suggests knowledge articles for self-service',
      technology: 'ServiceNow Now Assist for ITSM, integrated with CMDB and knowledge base',
      result: '• 60% reduction in average resolution time (30 min → 10 min)\n• Improved first-call resolution rate from 45% to 72%\n• Higher agent productivity and user satisfaction'
    }, {
      title: 'Knowledge Base Enrichment',
      challenge: 'Knowledge articles become outdated quickly, are hard to find, and are inconsistently formatted, leading to low self-service adoption.',
      solution: 'AI automatically:\n• Refines and formats articles for consistency\n• Updates metadata and tags for better searchability\n• Suggests new content based on recurring incidents and user queries\n• Translates articles into multiple languages',
      technology: 'ServiceNow AI Search and Knowledge Management, custom LLM integration',
      result: '• 60% faster knowledge discovery\n• 40% increase in self-service success rate\n• Reduced burden on knowledge managers'
    }, {
      title: 'Automated VPN Troubleshooting',
      challenge: 'High volume of repetitive VPN connectivity tickets consumes significant service desk resources.',
      solution: 'Conversational AI agent:\n• Detects common VPN issues through natural language\n• Analyzes user settings and logs autonomously\n• Restarts services and applies config updates without human intervention\n• Guides users through complex troubleshooting steps',
      technology: 'ServiceNow Virtual Agent, Microsoft Bot Framework',
      result: '• 40-60% reduction in VPN-related tickets\n• Auto-fix for common certificate errors\n• Instant resolution for end-users'
    }]
  }, {
    title: 'HR Service Delivery',
    useCases: [{
      title: 'Employee Self-Service & Inquiry Resolution',
      challenge: 'HR teams are overwhelmed with repetitive questions regarding benefits, leave policies, and payroll, delaying strategic initiatives.',
      solution: 'Intelligent Virtual Agent:\n• Auto-responds to FAQs using natural language understanding\n• Guides employees through complex HR processes (e.g., leave requests)\n• Personalizes responses based on employee role, location, and context\n• Seamlessly escalates complex issues to human agents with full context',
      technology: 'ServiceNow HRSD Virtual Agent, custom conversational AI',
      result: '• 50% reduction in tier-1 HR case volume\n• 24/7 availability for employee inquiries\n• Improved employee experience and satisfaction'
    }, {
      title: 'Zero-Touch Onboarding Automation',
      challenge: 'New hire onboarding requires 20+ manual steps across multiple disconnected systems (HRIS, IT, Facilities), leading to delays and errors.',
      solution: 'Agentic AI Workflow:\n• Orchestrates the entire onboarding process autonomously\n• Creates accounts, orders equipment, and assigns training modules\n• Provisions access in Active Directory and downstream applications\n• Generates and routes required documentation for electronic signature',
      technology: 'ServiceNow Automation Engine, Azure Logic Apps, custom agentic workflows',
      result: '• Onboarding completed in hours vs. days\n• Zero manual touchpoints for standard provisioning\n• Consistent, error-free new hire experience'
    }]
  }, {
    title: 'Customer Service',
    useCases: [{
      title: 'Intelligent Sentiment Analysis & Prioritization',
      challenge: 'Agents struggle to identify urgent or frustrated customers buried in high-volume ticket queues, leading to escalations and churn.',
      solution: 'AI Sentiment Engine:\n• Analyzes customer messages and interactions in real-time\n• Detects sentiment (frustration, urgency, satisfaction)\n• Automatically prioritizes and routes urgent cases to specialized teams\n• Alerts supervisors to potential escalations',
      technology: 'ServiceNow CSM Predictive Intelligence, AWS Comprehend',
      result: '• 30% reduction in customer escalations\n• Faster response times for critical issues\n• Improved customer satisfaction (CSAT) scores'
    }, {
      title: 'Automated Case Summarization',
      challenge: 'Agents spend excessive time reading long, complex case histories and email threads before they can formulate a response.',
      solution: 'Generative AI Summarization:\n• Generates concise, accurate summaries of entire case histories\n• Highlights key details, previous actions, and customer context\n• Drafts personalized responses based on the summary and knowledge base',
      technology: 'ServiceNow Now Assist for CSM, Azure OpenAI',
      result: '• 40% reduction in average handle time (AHT)\n• Faster case resolution\n• Improved agent productivity and reduced cognitive load'
    }]
  }, {
    title: 'Governance, Risk & Compliance',
    useCases: [{
      title: 'Intelligent Role Rationalization',
      challenge: 'Organizations suffer from permission sprawl, over-provisioned access, and complex role structures, creating significant audit and security risks.',
      solution: 'AI-Driven Access Analysis:\n• Analyzes current user access patterns and entitlements\n• Groups similar permissions and identifies anomalies\n• Proposes optimized, least-privilege role hierarchies\n• Generates automated migration plans for role consolidation',
      technology: 'SailPoint Predictive Identity, custom ML models',
      result: '• Accelerated audit compliance\n• Significantly reduced security and insider threat risk\n• Simplified access request processes'
    }, {
      title: 'Automated Contract Risk Flagging',
      challenge: 'Manual review of vendor contracts and compliance documents is time-consuming and prone to human error, leading to missed risk clauses.',
      solution: 'Document Intelligence:\n• Automatically extracts and classifies data from unstructured contracts\n• Highlights non-standard clauses, missing terms, and compliance gaps\n• Compares contract language against approved legal templates\n• Routes high-risk contracts for expedited legal review',
      technology: 'ServiceNow Document Intelligence, Azure AI Document Intelligence',
      result: '• 70% faster contract review cycles\n• Improved legal compliance and risk mitigation\n• Reduced reliance on manual data entry'
    }]
  }, {
    title: 'IT Operations',
    useCases: [{
      title: 'Intelligent Alert Correlation & Analysis',
      challenge: 'IT operations staff are overwhelmed by alert storms from multiple monitoring tools, struggling to identify the root cause of outages.',
      solution: 'AIOps Engine:\n• Ingests and correlates alerts from disparate monitoring systems\n• Identifies patterns and groups related alerts into single actionable incidents\n• Displays analysis with recommended remediation actions based on past events\n• Automatically suppresses noise and false positives',
      technology: 'ServiceNow ITOM Predictive AIOps, Splunk ITSI',
      result: '• 80% reduction in alert noise\n• Faster root cause identification and MTTR\n• Reduced cognitive load on IT operations teams'
    }, {
      title: 'Predictive Maintenance & Outage Prevention',
      challenge: 'System failures and infrastructure degradation cause unexpected downtime, impacting business operations and user productivity.',
      solution: 'Predictive Analytics:\n• Analyzes historical performance data and telemetry\n• Predicts potential hardware failures or capacity constraints before they occur\n• Triggers automated preventive actions (e.g., expanding storage, restarting services)\n• Generates proactive maintenance tasks',
      technology: 'ServiceNow Predictive Intelligence, custom ML models',
      result: '• 30% reduction in unplanned outages\n• Improved overall system availability and reliability\n• Shift from reactive to proactive IT operations'
    }]
  }];
  return <div className="w-full" data-id="element-415" data-mp="2y9pefpr3kzqg">
      {/* HERO SECTION */}
      <section className="section-navy hero-grid-pattern py-20 px-4" data-id="element-416" data-mp="2u95jmstzy87h">
        <div className="max-w-[1200px] mx-auto text-center" data-id="element-417" data-mp="2u9x2d32hwbgc">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-418" data-mp="26irqt03l85bu">
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-white mb-4 leading-tight max-w-4xl mx-auto" data-id="element-419" data-mp="21kq7qf65w60d">
              AI & Intelligent Automation for Federal Agencies and Enterprises
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-420" data-mp="2akwd1drphzog"></div>
            <p className="text-xl text-nrs-light font-bold mb-6 max-w-3xl mx-auto" data-id="element-421" data-mp="1mv22j2uv0n7d">
              Leverage AI, Large Language Models, and Intelligent Automation to
              Transform Business Operations, Improve Productivity, and Drive
              Innovation
            </p>
            <div className="text-lg text-blue-100 mb-10 max-w-4xl mx-auto leading-relaxed space-y-4" data-id="element-422" data-mp="3btibox08u5xn">
              <p data-id="element-423" data-mp="37rofp20ztq3t">
                New River Systems helps federal agencies and commercial
                enterprises harness the power of Generative AI, large language
                models (LLMs), and intelligent automation to transform
                operations. From conversational AI and virtual agents to agentic
                workflows and predictive intelligence, we design and implement
                AI solutions that deliver measurable business outcomes.
              </p>
              <p data-id="element-424" data-mp="2k2c9mb0ypy5q">
                Our AI implementations primarily leverage ServiceNow's GenAI
                platform — integrating Now Assist, virtual agents, and custom AI
                workflows into your existing ServiceNow environment. We also
                implement AI solutions across other enterprise platforms where
                your business needs demand.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-425" data-mp="ukylexzhm1w2">
              <Link to="/contact-us" className="btn-primary" data-id="element-426" data-mp="un1jm5asn1bv">
                Discuss Your AI Strategy
              </Link>
              <a href="#use-cases" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-427" data-mp="13ln1rzccsec4">
                View AI Use Cases
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 1: The AI Opportunity */}
      <section className="section-white" data-id="element-428" data-mp="fwc7n0pidi6y">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-429" data-mp="fv9d8isz8xma">
          <div className="text-center mb-12" data-id="element-430" data-mp="knkgnvmxf1jl">
            <h2 className="text-3xl font-bold text-nrs-navy mb-4" data-id="element-431" data-mp="koc0683918ti">
              Why Generative AI Matters for Your Enterprise
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-432" data-mp="3obvxdpqz0ih9"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-433" data-mp="vhnkiemrx8vr">
              Generative AI is fundamentally reshaping how organizations
              operate. Powered by large language models (LLMs) and intelligent
              agents, modern AI platforms enable employees to interact with
              systems using natural language, automate complex multi-step
              workflows, and make data-driven decisions faster than ever before.
              Whether embedded in workflow platforms like ServiceNow, integrated
              into customer service systems, or deployed as standalone
              intelligent agents, GenAI is transforming every aspect of
              enterprise operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-434" data-mp="hgb4k37m579v">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col items-center text-center" data-id="element-435" data-mp="1wy3dwe92qvaq">
              <ClockIcon className="h-12 w-12 text-nrs-orange mb-4" data-id="element-436" data-mp="20g0uxu7bnunw" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-437" data-mp="20iwsvij66jcc">
                3x Faster Response Times
              </h3>
              <p className="text-gray-600" data-id="element-438" data-mp="1qiwca4coqhbu">
                AI agents respond to employee requests three times faster than
                traditional service desk models, dramatically improving user
                satisfaction.
              </p>
            </motion.div>
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col items-center text-center" data-id="element-439" data-mp="2o650w87zg6i2">
              <DollarSignIcon className="h-12 w-12 text-nrs-orange mb-4" data-id="element-440" data-mp="2egbq39fnzvev" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-441" data-mp="2eeijtv8yrd7e">
                25-50% Cost Reduction
              </h3>
              <p className="text-gray-600" data-id="element-442" data-mp="36wme14gc0tib">
                Intelligent automation eliminates redundant processes and
                reduces operational costs by automating repetitive tasks and
                enabling self-service.
              </p>
            </motion.div>
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col items-center text-center" data-id="element-443" data-mp="1e7wwrp8znx7i">
              <TrendingUpIcon className="h-12 w-12 text-nrs-orange mb-4" data-id="element-444" data-mp="14cv8evmjez5z" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-445" data-mp="21xhfq8556ugs">
                40% Efficiency Improvement
              </h3>
              <p className="text-gray-600" data-id="element-446" data-mp="1x1smemjpu9sx">
                AI workflow automation streamlines processes, reduces manual
                tasks, and eliminates errors for measurable productivity gains.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: AI Capabilities We Implement */}
      <section className="bg-[#F7F7F7] py-20" data-id="element-447" data-mp="2fy1xd0zbmk8p">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-448" data-mp="2fx90crq5z93r">
          <div className="text-center mb-12" data-id="element-449" data-mp="2o8j42c9j6571">
            <h2 className="text-3xl font-bold text-nrs-navy mb-4" data-id="element-450" data-mp="2o9kcsa32qfll">
              AI Capabilities We Implement
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-451" data-mp="2x7cvlo8085dq"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-452" data-mp="326pig8yvlcgb">
              NRS implements enterprise AI capabilities across your technology
              stack, with primary expertise in ServiceNow's GenAI platform. Our
              AI solutions span conversational AI, intelligent automation,
              predictive analytics, and agentic workflows.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-453" data-mp="3kxgxnr5jnx2m">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-454" data-mp="3kzl4agutva6l">
              <SparklesIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-455" data-mp="3uw69p3arenay" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-456" data-mp="358kaxj6jbp9m">
                AI-Powered Assistance
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-457" data-mp="309z6f7k92jfd">
                Intelligent assistants that draft responses, summarize complex
                information, recommend next-best actions, and enable natural
                language interactions. Implemented primarily through ServiceNow
                Now Assist across IT, HR, and service delivery workflows,
                reducing resolution time by 60%.
              </p>
              <p className="text-xs text-gray-500 mt-auto font-semibold" data-id="element-458" data-mp="1rbv14vdazhj">
                Also available: Custom AI assistants using Azure OpenAI, AWS
                Bedrock, or standalone LLM implementations.
              </p>
            </motion.div>
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-459" data-mp="23if59t1cic1k">
              <BotIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-460" data-mp="e2cjabdo3fgw" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-461" data-mp="j0mfmpb4om43">
                Conversational AI & Virtual Agents
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-462" data-mp="4aozmwncr2pz">
                Deploy intelligent chatbots and virtual agents that understand
                natural language, guide employees through complex processes, and
                autonomously resolve common issues. Reduces service desk ticket
                volume by 40-60% through AI-powered self-service.
              </p>
              <p className="text-xs text-gray-500 mt-auto font-semibold" data-id="element-463" data-mp="3hjb9l37pc03t">
                Primary Platform: ServiceNow Virtual Agent. Also Supported:
                Microsoft Bot Framework, Dialogflow, custom LLM-powered agents.
              </p>
            </motion.div>
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-464" data-mp="1kpqamc37reuy">
              <BrainCircuitIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-465" data-mp="3ha5lzpumogk" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-466" data-mp="3je6e8sgb7cm">
                Predictive Intelligence & Machine Learning
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-467" data-mp="3pvjqvzyhfgd4">
                Leverage machine learning to predict outcomes, auto-route work,
                identify trends, and proactively prevent issues before they
                impact users. Improves first-call resolution and reduces
                escalations.
              </p>
              <p className="text-xs text-gray-500 mt-auto font-semibold" data-id="element-468" data-mp="3byoga32l1guy">
                Implementation: ServiceNow Predictive Intelligence, custom ML
                models, integration with existing analytics platforms.
              </p>
            </motion.div>
            <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-469" data-mp="157oyx6sm1wr5">
              <FileSearchIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-470" data-mp="1ho7um6t7lqzt" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-471" data-mp="1mfyaesax16ym">
                Document Intelligence & Data Extraction
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-472" data-mp="1rdhptvjcnps5">
                Automatically extract, classify, and summarize data from
                unstructured documents - contracts, forms, emails, meeting
                transcripts, technical documentation. Reduces manual data entry
                by 70%, improves accuracy, and flags compliance risks.
              </p>
              <p className="text-xs text-gray-500 mt-auto font-semibold" data-id="element-473" data-mp="17rv5znciu3bx">
                Technology: ServiceNow Document Intelligence, Azure AI Document
                Intelligence, custom NLP models.
              </p>
            </motion.div>
            <motion.div custom={4} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-474" data-mp="2j7tjc3eqfbiy">
              <SearchIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-475" data-mp="2o3gbwvwjyfjr" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-476" data-mp="2sx105w8cfv5r">
                AI-Powered Search & Knowledge Discovery
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-477" data-mp="2xwx45vh1jmlb">
                Intelligent search that understands user intent and context to
                deliver relevant answers from knowledge bases, documentation,
                policies, and enterprise content. Enables 60% faster information
                discovery and improves self-service success rates.
              </p>
              <p className="text-xs text-gray-500 mt-auto font-semibold" data-id="element-478" data-mp="2f2i6qaado1zl">
                Primary Platform: ServiceNow AI Search. Also Supported: Azure
                Cognitive Search, Elasticsearch with AI, custom semantic search.
              </p>
            </motion.div>
            <motion.div custom={5} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-479" data-mp="1evv6n0oro3yv">
              <WorkflowIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-480" data-mp="155jpudjzi0ak" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-481" data-mp="107s0nraq4sa2">
                Process Mining & Automation Discovery
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-482" data-mp="w7hsq93dfrsk">
                Maps existing workflows, identifies inefficiencies using pattern
                recognition, and recommends automation opportunities. Analyzes
                user behavior to identify repetitive tasks ripe for AI
                automation.
              </p>
              <p className="text-xs text-gray-500 mt-auto font-semibold" data-id="element-483" data-mp="2gli4cwxxlej6">
                Implementation: ServiceNow Process Optimization, integration
                with existing process mining tools, custom analytics.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 3: Agentic AI */}
      <section className="section-white py-20" data-id="element-484" data-mp="eozs3urdwsku">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-485" data-mp="3w4pu62ct6msq">
          <div className="text-center mb-12" data-id="element-486" data-mp="3w2mroiugz174">
            <h2 className="text-3xl font-bold text-nrs-navy mb-4" data-id="element-487" data-mp="4xvb6pi5exnd">
              Agentic AI: Autonomous Workflows
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-488" data-mp="soio5p6x8dgs"></div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-489" data-mp="xm10zqo27cu2">
              Agentic AI represents the next evolution of intelligent automation
              — autonomous systems that don't just answer questions but actually
              execute work. These AI agents can understand complex requests,
              orchestrate multi-step workflows across multiple systems, make
              intelligent decisions based on business rules, and take actions
              without human intervention. While traditional automation requires
              explicit programming of every step, agentic AI uses large language
              models and reasoning capabilities to adapt to new scenarios, learn
              from outcomes, and handle exceptions intelligently.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-white rounded-lg shadow-sm border-l-4 border-l-nrs-orange p-6 md:p-8 mb-12 max-w-4xl mx-auto border border-gray-100" data-id="element-490" data-mp="35vqlp9tibg5y">
            <h3 className="text-xl font-bold text-nrs-navy mb-4" data-id="element-491" data-mp="2ruxsqtiust4y">
              What is Agentic AI?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-492" data-mp="2n3o51ygv7zvp">
              <div data-id="element-493" data-mp="2n2d3m5aw747r">
                <h4 className="font-semibold text-gray-800 mb-3" data-id="element-494" data-mp="1ze3jr8x8oqj9">
                  Agentic AI Systems Can:
                </h4>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-495" data-mp="1ueqy2ri3tpi9">
                  <li className="flex items-start gap-2" data-id="element-496" data-mp="xof2zrdvf5hg">
                    <CheckIcon className="h-4 w-4 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-497" data-mp="xpzwddog8idq" />{' '}
                    Understand complex requests in natural language
                  </li>
                  <li className="flex items-start gap-2" data-id="element-498" data-mp="16na9z5oqojlt">
                    <CheckIcon className="h-4 w-4 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-499" data-mp="1bg3dxrcdvnwu" />{' '}
                    Break down tasks into multi-step workflows
                  </li>
                  <li className="flex items-start gap-2" data-id="element-500" data-mp="1ldptmg1ibjhv">
                    <CheckIcon className="h-4 w-4 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-501" data-mp="1lc6fow3bpkj9" />{' '}
                    Execute actions across multiple systems autonomously
                  </li>
                  <li className="flex items-start gap-2" data-id="element-502" data-mp="1ud4mfm55c65j">
                    <CheckIcon className="h-4 w-4 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-503" data-mp="3r6xzg8hhu2r6" />{' '}
                    Make intelligent decisions based on context and policies
                  </li>
                  <li className="flex items-start gap-2" data-id="element-504" data-mp="3olzycljmw6i8">
                    <CheckIcon className="h-4 w-4 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-505" data-mp="3onktac9v1rgm" />{' '}
                    Learn from outcomes to improve future performance
                  </li>
                  <li className="flex items-start gap-2" data-id="element-506" data-mp="3ex88ciy4vw9y">
                    <CheckIcon className="h-4 w-4 text-nrs-orange flex-shrink-0 mt-0.5" data-id="element-507" data-mp="39xn907f1xlwp" />{' '}
                    Escalate to humans only when necessary
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 p-4 rounded border border-gray-100" data-id="element-508" data-mp="6gdka9lzwgfx">
                <h4 className="font-semibold text-gray-800 mb-2" data-id="element-509" data-mp="1hadokqda25u">Example:</h4>
                <p className="text-sm text-gray-600 italic mb-2" data-id="element-510" data-mp="1jeprpyf67h7">
                  "Provision a new laptop for our new hire starting Monday"
                </p>
                <p className="text-sm text-gray-600" data-id="element-511" data-mp="2m8kjyei38qs6">
                  The agentic AI system automatically creates the onboarding
                  ticket, orders hardware, schedules delivery, provisions
                  accounts in Active Directory, assigns licenses, and updates
                  the new hire portal - all without human intervention.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-512" data-mp="29rjjtukobc0i">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card" data-id="element-513" data-mp="2emyz8qybxfz1">
              <h3 className="text-lg font-bold text-nrs-navy mb-4 border-b border-gray-100 pb-3" data-id="element-514" data-mp="25nbwa0pyc23v">
                IT Operations Self-Healing
              </h3>
              <div className="space-y-3 text-sm" data-id="element-515" data-mp="1qwl0yip5ikw0">
                <div data-id="element-516" data-mp="1vwzbq8i508xq">
                  <span className="font-semibold text-gray-800 block" data-id="element-517" data-mp="1vyk6nz8d5tw4">
                    Scenario:
                  </span>
                  <span className="text-gray-600" data-id="element-518" data-mp="1i03zy8ai43dz">
                    Network outage detected in Building 3
                  </span>
                </div>
                <div data-id="element-519" data-mp="13aivhy5zi19n">
                  <span className="font-semibold text-gray-800 block" data-id="element-520" data-mp="13bu6oj1rebas">
                    Agent Actions:
                  </span>
                  <ul className="text-gray-600 list-disc ml-4 space-y-1 mt-1" data-id="element-521" data-mp="yekb0lns13ei">
                    <li data-id="element-522" data-mp="1cda7q06op12c">
                      Analyzes logs from monitoring systems (Splunk, ServiceNow
                      ITOM, SolarWinds)
                    </li>
                    <li data-id="element-523" data-mp="1m94vtatofoci">
                      Correlates outage with recent change records in ServiceNow
                    </li>
                    <li data-id="element-524" data-mp="1hatmr2b7hhvc">Identifies root cause (failed switch)</li>
                    <li data-id="element-525" data-mp="1v1f60s4knttr">Creates incident ticket automatically</li>
                    <li data-id="element-526" data-mp="1r150tholt7fy">Routes to network team with diagnostic data</li>
                    <li data-id="element-527" data-mp="1r2zkkso52tig">
                      Monitors resolution status and updates knowledge base
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-2 rounded border border-gray-100" data-id="element-528" data-mp="202w2mbg0wu4j">
                  <span className="font-semibold text-gray-800 block" data-id="element-529" data-mp="37escnijpx3iv">
                    Technology Stack:
                  </span>
                  <span className="text-gray-600" data-id="element-530" data-mp="3bdsq0ljffdyh">
                    ServiceNow ITOM + Virtual Agent + Custom AI orchestration
                    layer
                  </span>
                </div>
                <div className="bg-green-50 p-2 rounded border border-green-100" data-id="element-531" data-mp="3ld0cpzc1wky9">
                  <span className="font-semibold text-green-800 block" data-id="element-532" data-mp="2nrcjfnaw6iqy">
                    Outcome:
                  </span>
                  <span className="text-green-700" data-id="element-533" data-mp="2sn1m3zi7kfrh">
                    70% faster incident resolution, reduced mean time to
                    recovery (MTTR)
                  </span>
                </div>
              </div>
            </motion.div>
            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card" data-id="element-534" data-mp="250biwuu4if5a">
              <h3 className="text-lg font-bold text-nrs-navy mb-4 border-b border-gray-100 pb-3" data-id="element-535" data-mp="29wa3e7cbzd0f">
                HR Onboarding
              </h3>
              <div className="space-y-3 text-sm" data-id="element-536" data-mp="1xh2c99d04ujx">
                <div data-id="element-537" data-mp="1xe6dxjx8s3uy">
                  <span className="font-semibold text-gray-800 block" data-id="element-538" data-mp="1sgekumkv2bp6">
                    Scenario:
                  </span>
                  <span className="text-gray-600" data-id="element-539" data-mp="1ngtcjljswqzs">
                    "New employee Jane Smith starting April 28"
                  </span>
                </div>
                <div data-id="element-540" data-mp="1ept00sbujron">
                  <span className="font-semibold text-gray-800 block" data-id="element-541" data-mp="1ep9fd3utap6x">
                    Agent Actions:
                  </span>
                  <span className="text-gray-600" data-id="element-542" data-mp="19qy4qqwp0ann">
                    Creates employee record, provisions accounts (email, VPN,
                    apps), orders equipment, assigns training modules, schedules
                    meetings, sends welcome package
                  </span>
                </div>
                <div className="bg-green-50 p-2 rounded border border-green-100" data-id="element-543" data-mp="vrgnqxpn21ou">
                  <span className="font-semibold text-green-800 block" data-id="element-544" data-mp="2rvcz530qapfx">
                    Outcome:
                  </span>
                  <span className="text-green-700" data-id="element-545" data-mp="2mznrsdigw492">
                    Zero-touch onboarding completed in minutes vs. days
                  </span>
                </div>
              </div>
            </motion.div>
            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card" data-id="element-546" data-mp="296690uxluvk3">
              <h3 className="text-lg font-bold text-nrs-navy mb-4 border-b border-gray-100 pb-3" data-id="element-547" data-mp="248mvxycn8otq">
                Procurement
              </h3>
              <div className="space-y-3 text-sm" data-id="element-548" data-mp="2d7ruh4o7gi5w">
                <div data-id="element-549" data-mp="2d6ym5z9llkln">
                  <span className="font-semibold text-gray-800 block" data-id="element-550" data-mp="2d5nevpgy1ups">
                    Scenario:
                  </span>
                  <span className="text-gray-600" data-id="element-551" data-mp="1lgkeiks6h4if">
                    "Order 50 laptops for Q2 refresh"
                  </span>
                </div>
                <div data-id="element-552" data-mp="1bb2kvf6asmt7">
                  <span className="font-semibold text-gray-800 block" data-id="element-553" data-mp="1bakh7xhb36b9">
                    Agent Actions:
                  </span>
                  <span className="text-gray-600" data-id="element-554" data-mp="11epsqlqeih0k">
                    Checks budget, validates approval, identifies preferred
                    vendor, generates PO, submits for approval, tracks delivery,
                    updates asset management
                  </span>
                </div>
                <div className="bg-green-50 p-2 rounded border border-green-100" data-id="element-555" data-mp="1z1y7lxvwcrtl">
                  <span className="font-semibold text-green-800 block" data-id="element-556" data-mp="1k4z52ofwjp5z">
                    Outcome:
                  </span>
                  <span className="text-green-700" data-id="element-557" data-mp="1p3ip2jaxqqvp">
                    Procurement cycle reduced from 2 weeks to 2 days
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4: Implementation Methodology */}
      <section className="bg-[#E6F0FF] py-20" data-id="element-558" data-mp="2gbtt3bcwuywl">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-559" data-mp="2gdmy6m7vlb2h">
          <div className="text-center mb-12" data-id="element-560" data-mp="2bdsbyvz1p9nr">
            <h2 className="text-3xl font-bold text-nrs-navy mb-4" data-id="element-561" data-mp="26lr03b5shky9">
              Our AI Implementation Methodology
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-562" data-mp="21nfniso40wcc"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-563" data-mp="1wo2ufq6nat1b">
              Successful AI adoption requires more than technology deployment.
              NRS brings a strategic, consulting-led approach to ensure your
              GenAI initiatives deliver measurable business outcomes.
            </p>
          </div>

          <div className="space-y-6 max-w-4xl mx-auto relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-nrs-orange before:to-transparent" data-id="element-564" data-mp="1ip3gbcgrdwje">
            {[{
            phase: 'Phase 1',
            title: 'AI Readiness Assessment & Use Case Discovery',
            timeline: '2-3 weeks',
            items: ['Evaluate current technology landscape and AI readiness', 'Identify high-value AI use cases across departments (IT, HR, finance, operations)', 'Assess data quality, governance, and integration requirements', 'Determine optimal platform strategy (ServiceNow GenAI, standalone AI, hybrid)', 'Define success metrics and ROI targets']
          }, {
            phase: 'Phase 2',
            title: 'AI Strategy & Architecture Design',
            timeline: '3-4 weeks',
            items: ['Prioritize use cases based on business impact, technical feasibility, ROI', 'Design AI solution architecture (platform selection, integration points)', 'Define governance framework (AI ethics, compliance, oversight)', 'Select technology stack:\n  • ServiceNow GenAI (Now Assist, Virtual Agent) for workflow automation\n  • Azure OpenAI or AWS Bedrock for custom LLM applications\n  • Standalone AI agents for cross-platform orchestration', 'Create phased implementation roadmap']
          }, {
            phase: 'Phase 3',
            title: 'Pilot Implementation',
            timeline: '6-8 weeks',
            items: ['Deploy AI solution for 1-2 high-impact use cases', 'Configure and train AI models for specific business context', 'Common pilots:\n  • IT service desk automation (ServiceNow Now Assist + Virtual Agent)\n  • HR onboarding automation (agentic workflows)\n  • Document processing and data extraction (Azure AI or ServiceNow Document Intelligence)', 'Measure pilot results against baseline metrics', 'Gather user feedback and refine']
          }, {
            phase: 'Phase 4',
            title: 'Scale and Optimize',
            timeline: '3-6 months',
            items: ['Expand GenAI to additional modules and use cases', 'Develop custom agentic AI workflows', 'Integrate external LLMs if needed (Azure OpenAI, AWS Bedrock)', 'Continuous model training and optimization']
          }, {
            phase: 'Phase 5',
            title: 'Adoption and Governance',
            timeline: 'Ongoing',
            items: ['User training and change management', 'AI Control Tower governance implementation', 'Ongoing monitoring, feedback loops, and improvements', 'Expansion to new departments and workflows']
          }].map((step, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-565" data-mp="1qzctzinqbfqd" data-mpi={i}>
                <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-nrs-orange text-white font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-566" data-mp="1vsz0et3aundq">
                  {i + 1}
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-6 rounded border border-gray-200 shadow-sm" data-id="element-567" data-mp="183mt5yrj8pe2">
                  <div className="flex justify-between items-center mb-3" data-id="element-568" data-mp="tvrmrkshn9ik">
                    <h4 className="font-bold text-nrs-navy text-lg" data-id="element-569" data-mp="txulqubmcczf">
                      {step.phase}: {step.title}
                    </h4>
                    <span className="text-xs font-semibold bg-orange-50 text-nrs-orange px-2 py-1 rounded" data-id="element-570" data-mp="k5f5c1fyzwgn">
                      {step.timeline}
                    </span>
                  </div>
                  <ul className="text-sm text-gray-600 list-disc ml-4 space-y-1 whitespace-pre-line" data-id="element-571" data-mp="1cv5j29dowwc8">
                    {step.items.map((item, j) => <li key={j} data-id="element-572" data-mp="17wc3mbeuev9f" data-mpi={j}>{item}</li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 5: Use Cases by Department */}
      <section id="use-cases" className="section-white py-20" data-id="element-573" data-mp="xx3bzp7g9sq8">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-574" data-mp="3nn72du8yr6lp">
          <div className="text-center mb-12" data-id="element-575" data-mp="3inu5shsail5x">
            <h2 className="text-3xl font-bold text-nrs-navy mb-4" data-id="element-576" data-mp="3in2jxyohw1su">
              GenAI Use Cases Across Your Enterprise
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-577" data-mp="1y3yq4u0x723q"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-578" data-mp="2355xwt32c1ae">
              From IT to HR to customer service, ServiceNow GenAI transforms
              every department.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-8 max-w-5xl mx-auto" data-id="element-579" data-mp="2cwsj9i947gqv">
            <div className="md:w-1/3 flex flex-col gap-2" data-id="element-580" data-mp="2ht93i1d71ayo">
              {tabs.map((tab, i) => <button key={i} onClick={() => setActiveTab(i)} className={`text-left px-4 py-3 rounded-md font-semibold transition-colors ${activeTab === i ? 'bg-nrs-navy text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`} data-id="element-581" data-mp="2luchsjfleyxs" data-mpi={i}>
                  {tab.title}
                </button>)}
            </div>
            <div className="md:w-2/3" data-id="element-582" data-mp="wiaj6zkhksqf">
              <AnimatePresence mode="wait" data-id="element-583" data-mp="11faq1cp5tzil">
                <motion.div key={activeTab} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.3
              }} className="space-y-6" data-id="element-584" data-mp="11g2hcbbq5b32">
                  {tabs[activeTab].useCases.map((useCase, i) => <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200" data-id="element-585" data-mp="384kzrnoxpm6f" data-mpi={i}>
                      <h3 className="text-xl font-bold text-nrs-navy mb-4" data-id="element-586" data-mp="2kegwxijklqgh">
                        {useCase.title}
                      </h3>
                      <div className="space-y-4 text-sm" data-id="element-587" data-mp="2ucxqmn9w8j68">
                        <div data-id="element-588" data-mp="2uabjuputu7p2">
                          <span className="font-semibold text-gray-800 block mb-1" data-id="element-589" data-mp="2pdl5q82l1m6a">
                            Business Challenge:
                          </span>
                          <span className="text-gray-600" data-id="element-590" data-mp="9kciiz8aewvf">
                            {useCase.challenge}
                          </span>
                        </div>
                        <div data-id="element-591" data-mp="c5ka0av3t2al">
                          <span className="font-semibold text-gray-800 block mb-1" data-id="element-592" data-mp="c8oivljikm7l">
                            AI Solution:
                          </span>
                          <span className="text-gray-600 whitespace-pre-line" data-id="element-593" data-mp="36yrugtjnpac">
                            {useCase.solution}
                          </span>
                        </div>
                        <div className="bg-gray-50 p-3 rounded border border-gray-100" data-id="element-594" data-mp="uwl6ms8r4lqz">
                          <span className="font-semibold text-gray-800 block mb-1" data-id="element-595" data-mp="zvwknthqhd4v">
                            Technology:
                          </span>
                          <span className="text-gray-600" data-id="element-596" data-mp="m4j7cxaf5bia">
                            {useCase.technology}
                          </span>
                        </div>
                        <div className="bg-green-50 p-3 rounded border border-green-100" data-id="element-597" data-mp="1duw8x01c3q0i">
                          <span className="font-semibold text-green-800 block mb-1" data-id="element-598" data-mp="1inpgrws3negt">
                            Business Outcome:
                          </span>
                          <span className="text-green-700 whitespace-pre-line" data-id="element-599" data-mp="2na8vs50vockq">
                            {useCase.result}
                          </span>
                        </div>
                      </div>
                    </div>)}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: AI Technology Platforms We Work With */}
      <section className="bg-[#F7F7F7] py-20" data-id="element-600" data-mp="3619tyqu6c438">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-601" data-mp="35zyqyt8jz0d6">
          <div className="text-center mb-12" data-id="element-602" data-mp="2rb2hqsurfhaz">
            <h2 className="text-3xl font-bold text-nrs-navy mb-4" data-id="element-603" data-mp="2wax9eymd0ax3">
              AI Technology Platforms We Work With
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-604" data-mp="3k09djk2txssj"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-605" data-mp="3orrguesbs5go">
              NRS implements AI solutions across multiple technology platforms
              based on your specific business needs, existing infrastructure,
              and use case requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-606" data-mp="1n2k7rqd5jxkv">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-607" data-mp="1n3v9lkn1ev9c">
              <div className="flex items-center gap-3 mb-4" data-id="element-608" data-mp="udn0sjee5n7f">
                <div className="bg-nrs-light p-3 rounded-lg" data-id="element-609" data-mp="pf3bc90l9tab">
                  <BrainCircuitIcon className="h-8 w-8 text-nrs-navy" data-id="element-610" data-mp="13e32bnbkqk9n" />
                </div>
                <h3 className="text-xl font-bold text-nrs-navy" data-id="element-611" data-mp="13bqdqrbb9bnm">
                  ServiceNow GenAI (Primary Platform)
                </h3>
              </div>
              <div className="text-gray-600 text-sm space-y-4 flex-grow" data-id="element-612" data-mp="2erx1msm69d2o">
                <p data-id="element-613" data-mp="2asek86tk7k4l">
                  Our deepest expertise lies in ServiceNow's GenAI capabilities,
                  where we implement:
                </p>
                <ul className="list-disc ml-5 space-y-1" data-id="element-614" data-mp="17j3f1ojc3uz3">
                  <li data-id="element-615" data-mp="1ceiqk9tvdesc">Now Assist (ITSM, ITOM, HRSD, CSM, DevOps)</li>
                  <li data-id="element-616" data-mp="1hedj0htamcfi">Virtual Agent and Conversational AI</li>
                  <li data-id="element-617" data-mp="1hcsm4ljiaheh">Predictive Intelligence</li>
                  <li data-id="element-618" data-mp="1lgs1v1wypc6s">Document Intelligence</li>
                  <li data-id="element-619" data-mp="1lee60qugjvy6">AI Search</li>
                  <li data-id="element-620" data-mp="oqrf18g5l5s4">Process Optimization</li>
                </ul>
                <div className="bg-gray-50 p-3 rounded mt-4" data-id="element-621" data-mp="ooeo45sf3l2x">
                  <p className="mb-2" data-id="element-622" data-mp="tq431wirgiwa">
                    <span className="font-semibold text-nrs-navy" data-id="element-623" data-mp="tmyag7axayvt">
                      Best For:
                    </span>{' '}
                    Organizations with existing ServiceNow deployments looking
                    to add AI capabilities to IT service management, HR service
                    delivery, customer service, or field operations workflows.
                  </p>
                  <p data-id="element-624" data-mp="9z8qgm98a2x5">
                    <span className="font-semibold text-nrs-navy" data-id="element-625" data-mp="2hkcekuv1r70">
                      Why ServiceNow:
                    </span>{' '}
                    Integrated platform, unified data model, enterprise-grade
                    security, federal compliance (FedRAMP), proven ROI.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-626" data-mp="3kpji61o275zk">
              <div className="flex items-center gap-3 mb-4" data-id="element-627" data-mp="3aw2rv7n4s90n">
                <div className="bg-nrs-light p-3 rounded-lg" data-id="element-628" data-mp="35y14iaio9njd">
                  <CloudIcon className="h-8 w-8 text-nrs-navy" data-id="element-629" data-mp="35w6kcyf85zgc" />
                </div>
                <h3 className="text-xl font-bold text-nrs-navy" data-id="element-630" data-mp="31ysb56rjnroc">
                  Azure, AWS & Google Cloud AI
                </h3>
              </div>
              <div className="text-gray-600 text-sm space-y-4 flex-grow" data-id="element-631" data-mp="2ryhz3ph8wqar">
                <p data-id="element-632" data-mp="vvn4ag9orffe">
                  For custom AI applications, standalone solutions, or
                  integration with non-ServiceNow systems, we leverage:
                </p>
                <ul className="list-disc ml-5 space-y-1" data-id="element-633" data-mp="10wj4ojutwjg2">
                  <li data-id="element-634" data-mp="m50r0spfz0zv">Azure OpenAI Service (GPT-4, ChatGPT, embeddings)</li>
                  <li data-id="element-635" data-mp="r4dg7k3sck5m">AWS Bedrock (Claude, Llama, Titan models)</li>
                  <li data-id="element-636" data-mp="r2k800dixrvi">Google Vertex AI (Gemini, PaLM)</li>
                  <li data-id="element-637" data-mp="d4cey3jlzapm">
                    Azure AI Services (Document Intelligence, Cognitive Search)
                  </li>
                  <li data-id="element-638" data-mp="h4mjdbrr5t2d">AWS AI Services (Comprehend, Textract, Lex)</li>
                </ul>
                <div className="bg-gray-50 p-3 rounded mt-4" data-id="element-639" data-mp="38sh3blhi1y8">
                  <p className="mb-2" data-id="element-640" data-mp="8599wj3mp4hn">
                    <span className="font-semibold text-nrs-navy" data-id="element-641" data-mp="8854pyk6jd1v">
                      Best For:
                    </span>{' '}
                    Custom AI applications, document processing outside
                    ServiceNow, conversational AI for websites/mobile apps,
                    cross-platform AI orchestration.
                  </p>
                  <p data-id="element-642" data-mp="3i3sx9usn9hbj">
                    <span className="font-semibold text-nrs-navy" data-id="element-643" data-mp="3i4chxj9oijt9">
                      Why Cloud AI:
                    </span>{' '}
                    Flexibility, latest LLM models, scalability, cost-effective
                    for specific use cases.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="card flex flex-col h-full" data-id="element-644" data-mp="4o4yu1wimqlk">
              <div className="flex items-center gap-3 mb-4" data-id="element-645" data-mp="jjb5mr3fqtfy">
                <div className="bg-nrs-light p-3 rounded-lg" data-id="element-646" data-mp="enc9uifs295i">
                  <NetworkIcon className="h-8 w-8 text-nrs-navy" data-id="element-647" data-mp="2lch6a0eha158" />
                </div>
                <h3 className="text-xl font-bold text-nrs-navy" data-id="element-648" data-mp="2gdnse6va484j">
                  Hybrid & Custom AI Solutions
                </h3>
              </div>
              <div className="text-gray-600 text-sm space-y-4 flex-grow" data-id="element-649" data-mp="3634ltx0kucn5">
                <p data-id="element-650" data-mp="3a2mvfwmy71ao">
                  Many enterprise AI initiatives require integration across
                  multiple platforms. We design hybrid solutions that:
                </p>
                <ul className="list-disc ml-5 space-y-1" data-id="element-651" data-mp="3k1ay9o6d39ts">
                  <li data-id="element-652" data-mp="3k2vt7ewl8us6">
                    Integrate ServiceNow GenAI with external LLMs (BYO-AI
                    approach)
                  </li>
                  <li data-id="element-653" data-mp="3t1r597mbcz3e">
                    Connect AI agents to legacy systems, databases, and
                    applications
                  </li>
                  <li data-id="element-654" data-mp="1pobu8sth33x">
                    Build custom agentic workflows that orchestrate across
                    platforms
                  </li>
                  <li data-id="element-655" data-mp="bmkalmswyava">
                    Implement AI governance and monitoring (AI Control Tower)
                  </li>
                </ul>
                <div className="bg-gray-50 p-3 rounded mt-4" data-id="element-656" data-mp="gl40tzeji8tg">
                  <p className="mb-2" data-id="element-657" data-mp="213xyeyqh854f">
                    <span className="font-semibold text-nrs-navy" data-id="element-658" data-mp="210tm1e2uy32o">
                      Best For:
                    </span>{' '}
                    Complex enterprise environments with multiple platforms,
                    regulatory requirements for AI governance, need for
                    specialized AI capabilities not available in single
                    platform.
                  </p>
                  <p data-id="element-659" data-mp="2jvgyawp7f328">
                    <span className="font-semibold text-nrs-navy" data-id="element-660" data-mp="2eukxwt429z1k">
                      Why Hybrid:
                    </span>{' '}
                    Best-of-breed approach, maximum flexibility, future-proof
                    architecture.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-[#E6F0FF] rounded-lg p-6 md:p-8 border border-blue-100 max-w-4xl mx-auto" data-id="element-661" data-mp="32knkitp7icss">
            <h3 className="text-xl font-bold text-nrs-navy mb-4" data-id="element-662" data-mp="264azqzek7rob">
              Platform Selection Guidance
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-663" data-mp="2ay5dznbebeg0">
              Not sure which platform is right for your AI initiatives? Our AI
              Readiness Assessment includes platform recommendation based on:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700" data-id="element-664" data-mp="2ptuxnet3zfhk">
              <div className="flex items-start gap-2" data-id="element-665" data-mp="2tuwwvvn0zrjj">
                <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-666" data-mp="2tun82bdpvqff" />{' '}
                Your existing technology stack (do you already have ServiceNow?)
              </div>
              <div className="flex items-start gap-2" data-id="element-667" data-mp="2kt75gpt58slr">
                <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-668" data-mp="14gic1mf4ootb" />{' '}
                Use case requirements (workflow automation vs. custom AI)
              </div>
              <div className="flex items-start gap-2" data-id="element-669" data-mp="19d8u2fahtuhd">
                <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-670" data-mp="11urf3tjulnh0" />{' '}
                Data governance and compliance needs (federal, HIPAA, etc.)
              </div>
              <div className="flex items-start gap-2" data-id="element-671" data-mp="1ax0lq8bzwwp4">
                <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-672" data-mp="1aueg4e8o0r9j" />{' '}
                Integration complexity (number of systems involved)
              </div>
              <div className="flex items-start gap-2" data-id="element-673" data-mp="i8mq1z8dqcgq">
                <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-674" data-mp="e6hwt1lufhum" />{' '}
                Budget and timeline constraints
              </div>
              <div className="flex items-start gap-2" data-id="element-675" data-mp="rz7kdah0sutu">
                <CheckIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-676" data-mp="n6ef8lhn3kh8" />{' '}
                Long-term AI strategy and roadmap
              </div>
            </div>
            <p className="text-gray-700 mt-4 font-semibold" data-id="element-677" data-mp="3vkda1jsvkzt5">
              We help you choose the right platform architecture - whether
              that's ServiceNow GenAI, cloud AI, or a hybrid approach.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 6: Why NRS */}
      <section className="section-navy" data-id="element-678" data-mp="2jv2w63otjh38">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-679" data-mp="2eyukj09ty60t">
          <div className="text-center mb-12" data-id="element-680" data-mp="2awhfke2wqc53">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-681" data-mp="2axsjqf09llwq">
              Why Choose NRS for AI & Intelligent Automation
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-682" data-mp="32ndq381hw4p7"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-683" data-mp="2ylsahexktcbb">
            <div className="flex items-start gap-4" data-id="element-684" data-mp="2ynmvet8ul4fe">
              <CheckCircleIcon className="h-8 w-8 text-nrs-orange flex-shrink-0 mt-1" data-id="element-685" data-mp="2tlnsfkh0s9iz" />
              <div data-id="element-686" data-mp="1n6wrjgrwcooy">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-687" data-mp="1n87y1ocq8ajr">
                  Enterprise Platform Expertise
                </h3>
                <p className="text-blue-100 text-sm leading-relaxed" data-id="element-688" data-mp="1i9wn1aap3tzy">
                  20+ years implementing enterprise workflow platforms with deep
                  expertise in ServiceNow (ITSM, HRSD, SPM, CSM, Security
                  Operations) plus experience with Azure, AWS, and custom AI
                  solutions. We understand how AI integrates into complex
                  enterprise technology stacks.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-689" data-mp="gfr106bcqqo3">
              <CheckCircleIcon className="h-8 w-8 text-nrs-orange flex-shrink-0 mt-1" data-id="element-690" data-mp="leassncmmwod" />
              <div data-id="element-691" data-mp="qdne33nunvou">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-692" data-mp="qf89swlwhkoa">
                  Federal AI Compliance Knowledge
                </h3>
                <p className="text-blue-100 text-sm leading-relaxed" data-id="element-693" data-mp="udo1vewu9j8k">
                  Experience implementing AI in federal environments with strict
                  security, compliance, and ATO requirements. We ensure GenAI
                  deployments meet NIST AI Risk Management Framework, FedRAMP,
                  and agency-specific AI governance standards for responsible AI
                  deployment.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-694" data-mp="ce4lz01b90zr">
              <CheckCircleIcon className="h-8 w-8 text-nrs-orange flex-shrink-0 mt-1" data-id="element-695" data-mp="8bzpltjh9q9f" />
              <div data-id="element-696" data-mp="mbizaqrxt9nu">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-697" data-mp="mazdv032w352">
                  AI Governance & Responsible Deployment
                </h3>
                <p className="text-blue-100 text-sm leading-relaxed" data-id="element-698" data-mp="hc60d7nskc4w">
                  We apply rigorous governance to AI implementations including
                  bias detection, explainability, data privacy, and ethical AI
                  principles. Our governance frameworks ensure responsible AI
                  deployment with proper controls, monitoring, and oversight -
                  critical for federal and regulated industries.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-id="element-699" data-mp="1jpinr48ctpjh">
              <CheckCircleIcon className="h-8 w-8 text-nrs-orange flex-shrink-0 mt-1" data-id="element-700" data-mp="1oo27qz3e0r97" />
              <div data-id="element-701" data-mp="1aokdi47bolsg">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-702" data-mp="1amzkwk4kjcx8">
                  Business Outcome Focus
                </h3>
                <p className="text-blue-100 text-sm leading-relaxed" data-id="element-703" data-mp="1ent8tdi0grq4">
                  We don't implement AI for the sake of AI. Every GenAI
                  deployment is tied to measurable business outcomes: cost
                  reduction, productivity improvement, user satisfaction, or
                  compliance achievement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: Getting Started */}
      <section className="bg-[#E6F0FF] py-20" data-id="element-704" data-mp="i0ol3bosdg1h">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-705" data-mp="mygn4yj5aije">
          <div className="text-center mb-12" data-id="element-706" data-mp="mxnbpk98r4ux">
            <h2 className="text-3xl font-bold text-nrs-navy mb-4" data-id="element-707" data-mp="eyabxg6i14pe">
              Ready to Transform Your Workflows with AI?
            </h2>
            <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-708" data-mp="9z7g8mcesau3"></div>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto" data-id="element-709" data-mp="56mqbvwmihvu">
              Start with an AI Readiness Assessment to identify high-value use
              cases and create a phased roadmap for GenAI adoption.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-710" data-mp="3saard3ut6rew">
            <div className="bg-white p-8 rounded-lg shadow-sm text-center border border-gray-200 flex flex-col" data-id="element-711" data-mp="3nbzgqqwowcvm">
              <ClipboardCheckIcon className="h-12 w-12 text-nrs-orange mx-auto mb-4" data-id="element-712" data-mp="3igk2hxurseyo" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-713" data-mp="3eegszernvkg1">
                AI Readiness Assessment
              </h3>
              <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-714" data-mp="1gkrw8uged1tt">
                2-3 week evaluation of your enterprise AI readiness including
                technology landscape, data governance, use case discovery, and
                platform recommendations. Identify highest-value AI
                opportunities across IT, HR, operations, and customer service.
              </p>
              <Link to="/contact-us" className="text-nrs-orange font-bold text-sm hover:underline" data-id="element-715" data-mp="34b1x5fd6e063">
                Learn More →
              </Link>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm text-center border border-gray-200 flex flex-col" data-id="element-716" data-mp="2bll88rorl1em">
              <RocketIcon className="h-12 w-12 text-nrs-orange mx-auto mb-4" data-id="element-717" data-mp="2gjmshfxxf6ro" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-718" data-mp="2kk6ouhaoe6cr">
                Pilot Implementation
              </h3>
              <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-719" data-mp="2plcl2p3jobr1">
                6-8 week deployment of AI solution for one high-impact use case
                - whether ServiceNow GenAI, cloud AI platform, or custom
                intelligent agent. Prove ROI before scaling.
              </p>
              <Link to="/contact-us" className="text-nrs-orange font-bold text-sm hover:underline" data-id="element-720" data-mp="20v5ggm1lthn3">
                View Approach →
              </Link>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm text-center border border-gray-200 flex flex-col" data-id="element-721" data-mp="2au3csb59ufgn">
              <BarChartIcon className="h-12 w-12 text-nrs-orange mx-auto mb-4" data-id="element-722" data-mp="25wl0qbvyjk4f" />
              <h3 className="text-xl font-bold text-nrs-navy mb-3" data-id="element-723" data-mp="1i7iltbspk5dm">
                Enterprise Rollout
              </h3>
              <p className="text-gray-600 text-sm mb-6 flex-grow" data-id="element-724" data-mp="1d8yyp82donjo">
                Expand GenAI across departments with agentic AI workflows,
                governance, and continuous optimization
              </p>
              <Link to="/contact-us" className="text-nrs-orange font-bold text-sm hover:underline" data-id="element-725" data-mp="1n3h36ffmcn4j">
                Discuss Scaling →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: Call to Action */}
      <section className="section-navy py-20" data-id="element-726" data-mp="3636xghig7unu">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-727" data-mp="3625cnlbmrtsx">
          <h3 className="text-3xl font-bold text-white mb-4" data-id="element-728" data-mp="3aw9j83gzhzth">
            Let's Discuss Your AI Strategy
          </h3>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto" data-id="element-729" data-mp="2x1qmo8nq1qj3">
            Whether you're exploring Now Assist, building virtual agents, or
            implementing agentic AI workflows, our team is ready to help you
            harness ServiceNow GenAI for measurable business outcomes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-730" data-mp="3fu1k3mzlww9t">
            <Link to="/contact-us" className="btn-primary" data-id="element-731" data-mp="3fw5khuyard5c">
              Request AI Readiness Assessment
            </Link>
            <Link to="/contact-us" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-732" data-mp="24f5vb878xefj">
              Contact Our AI Team
            </Link>
          </div>
        </div>
      </section>
    </div>;
}